import { FC, useMemo } from "react";

import { Box, chakra, HStack } from "@chakra-ui/react";
import { format, isValid, parse } from "date-fns";
import { i18n, Trans, useTranslation } from "next-i18next";

import { FullWidthVStack } from "@components/common";
import DatePicker from "@components/DatePicker";
import Icon from "@components/Icon";
import Loader from "@components/Loader";
import Message from "@components/Message";
import { RadioGroup } from "@components/Radio";
import TimePicker from "@components/TimePicker";
import responsiveStyle from "@lib/theme/responsiveStyle";
import { DATE } from "@utils/constants";

import { LabelPrint, OrderFields } from "../../constants";
import useOrderFieldsLoading from "../../context/useOrderFieldsLoading";
import usePickupTimeFrames from "../../hooks/sender/usePickupTimeFrames";
import useOrderFormik from "../../hooks/useOrderFormik";
import getIsDeliverySenderFromBalticCountry from "../../utils/getIsDeliverySenderFromBalticCountry";
import SectionTitle from "../common/SectionTitle";

const DeliveryDetails: FC = () => {
  const { t } = useTranslation();

  const {
    values: {
      [OrderFields.IS_FROM_ORIGIN]: isFromOrigin,
      [OrderFields.LABEL_PRINT]: labelPrintValue,
      [OrderFields.PICKUP_DATE]: pickupDate,
      [OrderFields.SENDER_ZIP_CODE]: senderZipCode,
      [OrderFields.STARTING_COUNTRY]: startingCountry,
    },
    setFieldValue,
    setFieldTouched,
    setValues,
    errors: { [OrderFields.LABEL_PRINT]: labelPrintError, [OrderFields.PICKUP_DATE]: pickupDateError },
    touched: { [OrderFields.LABEL_PRINT]: isLabelPrintTouched, [OrderFields.PICKUP_DATE]: isPickupDateTouched },
  } = useOrderFormik();

  const isSenderCountryBaltic = getIsDeliverySenderFromBalticCountry(startingCountry!);
  const { isFieldLoading } = useOrderFieldsLoading();

  const { hasDates, minDate, maxDate, isDateAvailable, availableTimesFrom, availableTimesTo } =
    usePickupTimeFrames(isFromOrigin);

  const pickupDateValue = useMemo(() => {
    const parsedValue = pickupDate ? parse(pickupDate, DATE.DATE_TEMPLATE, new Date()) : undefined;
    return isValid(parsedValue) ? parsedValue : undefined;
  }, [pickupDate]);

  const handlePickupDateChanged = (value: any) => {
    setValues((oldValues) => ({
      ...oldValues,
      [OrderFields.PICKUP_DATE]: value ? format(value, DATE.DATE_TEMPLATE) : "",
      [OrderFields.PICKUP_TIME_FROM]: "",
      [OrderFields.PICKUP_TIME_TILL]: "",
    }));
  };

  const handleTouchDatePicker = () => {
    setFieldTouched(OrderFields.PICKUP_DATE, true);
  };

  const handlePickupTimeFromChanged = () => {
    setFieldValue(OrderFields.PICKUP_TIME_TILL, "");
  };

  return (
    <FullWidthVStack spacing="0.5rem">
      <SectionTitle>{t("order-sender-delivery_details")}</SectionTitle>
      {isFromOrigin && (
        <Box>
          <RadioGroup
            isFormik={false}
            onChange={(value) => setFieldValue(OrderFields.LABEL_PRINT, value)}
            message={isLabelPrintTouched ? labelPrintError : undefined}
            isInvalid={!!labelPrintError && !!isLabelPrintTouched}
            isGrey
            id="order-sender-label_print"
            variant="dark"
            name={OrderFields.LABEL_PRINT}
            initialValue={labelPrintValue!}
            options={[
              {
                label: (
                  <chakra.span fontSize={responsiveStyle({ mobile: "0.875rem", desktop: "1rem" })}>
                    <Trans
                      i18n={i18n!}
                      i18nKey="order-sender-i_would_like_label_brought_to_me"
                      components={{ strong: <chakra.span fontWeight="bold" /> }}
                    />
                  </chakra.span>
                ),
                value: LabelPrint.COURIER,
              },
              {
                label: (
                  <chakra.span fontSize={responsiveStyle({ mobile: "0.875rem", desktop: "1rem" })}>
                    {t("order-sender-i_will_print_label_myself")}
                  </chakra.span>
                ),
                value: LabelPrint.PRINT,
              },
            ]}
          />
        </Box>
      )}
      <Box width={responsiveStyle({ mobile: "100%", desktop: "50%" })}>
        <Loader isLoading={isFieldLoading(OrderFields.PICKUP_DATE)}>
          <FullWidthVStack spacing="0.5rem">
            <Box>
              <DatePicker
                isFormik={false}
                id="order-sender-pickup_date"
                mode="single"
                name={OrderFields.PICKUP_DATE}
                label={t("order-sender-pickup_date")}
                dateTemplate={DATE.DATE_TEMPLATE}
                minDate={minDate}
                maxDate={maxDate}
                filterDate={isDateAvailable}
                isDisabled={!senderZipCode || !hasDates}
                value={pickupDateValue}
                onClose={handleTouchDatePicker}
                onBlur={handleTouchDatePicker}
                onChange={handlePickupDateChanged}
                message={isPickupDateTouched ? pickupDateError : undefined}
                isInvalid={!!pickupDateError && isPickupDateTouched}
              />
            </Box>
            {!isSenderCountryBaltic && (
            <Message align="start" icon={<Icon icon="info" size="1.25rem" />} sx={{ py: "0.7rem" }}>
              <Box>{t("order-sender-collection_can_take_longer")}</Box>
            </Message>
            )}
            {isFromOrigin && (
              <Box>
                <HStack>
                  <Box flex="1">
                    <TimePicker
                      id="order-sender-pickup_time_from"
                      name={OrderFields.PICKUP_TIME_FROM}
                      options={availableTimesFrom || []}
                      label={t("order-sender-pickup_time_from")}
                      isDisabled={!pickupDate || !availableTimesFrom?.length}
                      onChange={handlePickupTimeFromChanged}
                    />
                  </Box>
                  <Box flex="1">
                    <TimePicker
                      id="order-sender-pickup_time_to"
                      name={OrderFields.PICKUP_TIME_TILL}
                      options={availableTimesTo || []}
                      label={t("order-sender-pickup_time_till")}
                      isDisabled={!pickupDate || !availableTimesTo?.length}
                    />
                  </Box>
                </HStack>
              </Box>
            )}
          </FullWidthVStack>
        </Loader>
      </Box>
    </FullWidthVStack>
  );
};
export default DeliveryDetails;
